/**
 * Small presentational helpers: separator, skeleton, empty state and stats.
 *
 * They exist so that a page never has to invent its own spacing for "nothing
 * here yet" or for a label/value pair.
 */

import type { ComponentProps, ReactNode } from 'react'

import { cn } from '../../lib/format'

export function Separator({ className, ...props }: ComponentProps<'div'>) {
  return <div role="separator" className={cn('bg-border h-px w-full', className)} {...props} />
}

export function Skeleton({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('bg-muted animate-pulse rounded-md', className)} {...props} />
}

export interface EmptyStateProps {
  title: string
  description?: ReactNode
  action?: ReactNode
  className?: string
}

export function EmptyState({ title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'border-border flex flex-col items-center gap-2 rounded-xl border border-dashed p-8 text-center',
        className,
      )}
    >
      <p className="text-sm font-medium">{title}</p>
      {description ? <p className="text-muted-foreground text-sm">{description}</p> : null}
      {action}
    </div>
  )
}

export interface StatProps {
  label: string
  value: ReactNode
  hint?: ReactNode
  className?: string
}

/** One label/value pair inside a details grid. */
export function Stat({ label, value, hint, className }: StatProps) {
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-sm font-medium">{value}</span>
      {hint ? <span className="text-muted-foreground text-xs">{hint}</span> : null}
    </div>
  )
}

export function StatGrid({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('grid gap-3 sm:grid-cols-2', className)} {...props} />
}
